import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, Package, TrendingUp, Users, Mail } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const PromotionOptInsView = () => {
  const { promotionId } = useParams();
  const navigate = useNavigate();
  const { apiCall } = useAuth();
  const [loading, setLoading] = useState(true);
  const [promotion, setPromotion] = useState(null);
  const [optIns, setOptIns] = useState([]);

  useEffect(() => {
    fetchData();
  }, [promotionId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch promotion details
      const promoResponse = await apiCall(`/api/marketing/promotions/${promotionId}`);
      setPromotion(promoResponse.data);
      
      // Fetch opt-ins for this promotion
      const optInsResponse = await apiCall(`/api/marketing/promotions/${promotionId}/opt-ins`);
      setOptIns(optInsResponse.data || []);
    } catch (error) {
      console.error('Error fetching opt-ins:', error);
      toast.error('Failed to load opt-in data');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <p className="text-gray-500">Loading opt-in data...</p>
        </div>
      </DashboardLayout>
    );
  }

  if (!promotion) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <p className="text-gray-500">Promotion not found</p>
        </div>
      </DashboardLayout>
    );
  }

  const totalVendors = optIns.length;
  const totalProducts = optIns.reduce((sum, opt) => sum + (opt.total_products || 0), 0);
  const avgDiscount = totalVendors > 0
    ? optIns.reduce((sum, opt) => sum + (opt.discount_offered || 0), 0) / totalVendors
    : 0;

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="outline" onClick={() => navigate('/marketing/promotions')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-3xl font-bold">Vendor Opt-Ins</h1>
            <p className="text-gray-500 mt-1">{promotion.name}</p>
          </div>
        </div>

        {/* Promotion Summary */}
        <Card>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <p className="text-sm text-gray-500">Promotion Period</p>
                <p className="font-semibold">
                  {formatDate(promotion.start_timestamp)} - {formatDate(promotion.end_timestamp)}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Min Discount Required</p>
                <p className="text-2xl font-bold text-blue-600">{promotion.discount_mandate_min}%</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Status</p>
                <Badge className="mt-1">{promotion.status}</Badge>
              </div>
              <div>
                <p className="text-sm text-gray-500">Theme</p>
                <p className="font-semibold">{promotion.theme}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Vendors</p>
                  <p className="text-3xl font-bold">{totalVendors}</p>
                </div>
                <Users className="h-10 w-10 text-blue-600" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Products</p>
                  <p className="text-3xl font-bold">{totalProducts}</p>
                </div>
                <Package className="h-10 w-10 text-green-600" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Avg Discount</p>
                  <p className="text-3xl font-bold">{avgDiscount.toFixed(1)}%</p>
                </div>
                <TrendingUp className="h-10 w-10 text-purple-600" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Vendor Opt-Ins List */}
        <Card>
          <CardHeader>
            <CardTitle>Participating Vendors</CardTitle>
          </CardHeader>
          <CardContent>
            {optIns.length === 0 ? (
              <div className="text-center py-12">
                <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">No vendors have opted in yet</p>
              </div>
            ) : (
              <div className="space-y-4">
                {optIns.map((optIn, index) => (
                  <div key={optIn.id || index} className="border rounded-lg p-4 hover:bg-gray-50">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-3">
                          <h3 className="font-semibold text-lg">{optIn.vendor_name || 'Vendor'}</h3>
                          <Badge variant="outline">{optIn.status || 'Active'}</Badge>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
                          <div>
                            <p className="text-sm text-gray-500">Products Committed</p>
                            <p className="text-xl font-semibold">{optIn.total_products || 0}</p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-500">Avg Discount</p>
                            <p className="text-xl font-semibold text-green-600">{(optIn.discount_offered || 0).toFixed(1)}%</p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-500">Opted In</p>
                            <p className="text-sm font-medium">{optIn.created_at ? formatDate(optIn.created_at) : 'N/A'}</p>
                          </div>
                        </div>

                        {optIn.products_committed && optIn.products_committed.length > 0 && (
                          <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                            <p className="text-sm font-medium text-gray-700 mb-2">Products:</p>
                            <div className="space-y-1">
                              {optIn.products_committed.slice(0, 5).map((product, idx) => (
                                <div key={product.id || product.name || `${product.title || "product"}-${idx}`} className="text-sm text-gray-600 flex justify-between">
                                  <span>• {product.product_title}</span>
                                  <span className="font-medium">
                                    ₹{product.committed_price} 
                                    <span className="text-green-600 ml-2">({product.discount_percentage}% off)</span>
                                  </span>
                                </div>
                              ))}
                              {optIn.products_committed.length > 5 && (
                                <p className="text-sm text-blue-600 mt-1">
                                  + {optIn.products_committed.length - 5} more products
                                </p>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                      
                      <Button variant="outline" size="sm">
                        <Mail className="h-4 w-4 mr-2" />
                        Contact
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default PromotionOptInsView;
